import { useMemo } from "react";
import styled, { css, keyframes } from "styled-components";
import { BASIC_DARK_GRAY, BASIC_GRAY } from "../../../constants/colors";
import { Menu, MenuInCart } from "../../../ducks/menu";
import { Button } from "../../atoms/Button";
import { Select } from "../../atoms/Select";
import { MenuCountSelect } from "../../molecules/menu/MenuCountSelect";
import { MenuImage } from "../../molecules/menu/MenuImage";
import { MenuPrice } from "../../molecules/menu/MenuPrice";

type Props = {
  menu: Menu;
  menuInCart: MenuInCart;
  selectedLimitedMenus: Menu[];
  animate: boolean;
  onFavorite: (favorite: boolean) => void;
  onUpdateItem: (
    amount: number,
    optionId?: number,
    optionName?: string,
  ) => void;
  onRemove: () => void;
};

export const OrderItem: React.FC<Props> = ({
  menu,
  menuInCart,
  selectedLimitedMenus,
  animate,
  onFavorite,
  onUpdateItem,
  onRemove,
}) => {
  const shouldDisable = useMemo(() => {
    return menu.isPreorder && !menu.isEnableButton;
  }, [menu]);

  const isLimitedMenuSelected = useMemo(() => {
    if (!menu.isLimitedMenu) return false;

    return selectedLimitedMenus.some(
      (selectedMenu) => selectedMenu.code === menu.code,
    );
  }, [selectedLimitedMenus]);

  return (
    <CartItemContainer>
      <MenuInformation>
        {/* TODO: add animation */}
        <AnimatedMenuImage $animate={false}>
          <MenuImage
            id={menu.id}
            imageUrl={menu.imageUrl}
            isFavorite={menu.isFavorite}
            onFavorite={onFavorite}
            width={90}
            height={90}
            onClickImage={() => {}}
          />
        </AnimatedMenuImage>

        <MenuDetail>
          <MenuName>{menu.name}</MenuName>

          <MenuPrice
            price={menu.price}
            priceWithTax={menu.priceWithTax}
            isOriginalPrice={!!menu.campaign}
            size="md"
          />

          {menu.campaign && (
            <>
              <MenuPrice
                price={menu.campaign.price}
                priceWithTax={menu.campaign.priceWithTax}
                isDiscountPrice
                size="md"
              />
              <MenuCampaignDetail>{menu.campaign.details}</MenuCampaignDetail>
            </>
          )}

          {menu.options.length > 0 && (
            <SelectContainer>
              <Select
                name="menuOption"
                padding="2px 0 2px 5px"
                top="30px"
                placeholder="-"
                width="auto !important"
                onChange={(optionName, optionId) =>
                  onUpdateItem(
                    menuInCart.amount,
                    optionId === "" ? undefined : optionId,
                    optionName === "" ? undefined : optionName,
                  )
                }
                options={menu.options.map((option) => ({
                  id: option.id,
                  name: option.name,
                  value: option.id,
                }))}
                defaultValue={menuInCart.optionName}
              />
            </SelectContainer>
          )}

          {menu.isPreorder && (
            <MenuPreorderMessage>{menu.preorderMessage}</MenuPreorderMessage>
          )}
        </MenuDetail>
      </MenuInformation>

      <CartActions>
        <MenuCountSelect
          initialCount={menuInCart.amount}
          onChange={(count) =>
            onUpdateItem(count, menuInCart.optionId, menuInCart.optionName)
          }
          disabled={shouldDisable}
          limited={menu.isLimitedMenu}
          limitedDisabled={isLimitedMenuSelected}
          size="sm"
        />

        <ButtonContainer>
          <Button
            size="xxs"
            btnStyle={shouldDisable ? "disabled" : "secondary"}
            text="削除"
            disabled={shouldDisable}
            onClick={onRemove}
          />
        </ButtonContainer>
      </CartActions>
    </CartItemContainer>
  );
};

const CartItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 40px 0;
`;

const MenuInformation = styled.div`
  display: flex;
  flex-direction: row;
`;

const MenuDetail = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  text-align: left;
  margin: 0 14px;
`;

const MenuName = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  cursor: pointer;
`;

const MenuCampaignDetail = styled.div`
  text-align: left;
  font-size: 12px;
  color: ${BASIC_DARK_GRAY};
  line-height: 2em;
  word-wrap: break-word;
  white-space: normal;
`;

const MenuPreorderMessage = styled.div`
  font-size: 12px;
  color: ${BASIC_GRAY};
`;

const CartActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-right: 14px;
`;

const falling = keyframes`
  0% {
    transform: translate(0, 0) scale(1,1) rotate(0deg);
    opacity: 1;
  }
  25% {
    transform: translate(35px, -90px) scale(1,1) rotate(30deg);
    opacity: 1;
  }
  50% {
    transform: translate(100px, 200px) scale(0,0) rotate(180deg);
    opacity: 0;
  }
  75% {
    transform: translate(125px, 200px) scale(0,0) rotate(180deg);
    opacity: 0;
  }
  76% {
    transform: translate(0, 0) scale(0,0) rotate(0deg);
    opacity: 0;
  }
  100% {
    transform: translate(0, 0) scale(0,0) rotate(0deg);
    opacity: 1;
  }
`;

const AnimatedMenuImage = styled.div<{ $animate: boolean }>`
  padding-right: 20px;
  ${(props) =>
    props.$animate &&
    css`
      animation: ${falling} 1.5s ease-in-out 0s 1 alternate none running;
    `};
`;

const ButtonContainer = styled.div`
  button {
    padding: 9px 16px;
    margin-left: 42px;
    letter-spacing: 0;
    width: auto;
    font-size: 12px;
    line-height: 1em;
    color: ${BASIC_DARK_GRAY};
    font-weight: 300;
    border-radius: 6px;
  }
`;

const SelectContainer = styled.div`
  margin-bottom: 15px;
`;
